export interface DocMetadata {
  title?: string;       // Custom page title for <title>
  heading?: string;     // Custom H1 heading for the page layout
  description: string;  // Curated, unique meta description
}

export const docMeta: Record<string, DocMetadata> = {
  // ==================== 1. OVERVIEW ====================
  '/docs/overview': {
    title: 'Solarch Documentation — Overview',
    heading: 'Overview',
    description: 'Get started with Solarch, the open-source TypeScript Backend-as-a-Service packaging SQLite, Express, WebSocket, and Auth into a single npm package.',
  },
  '/docs/overview/introduction': {
    title: 'Solarch Architecture & Philosophy — Introduction',
    heading: 'Introduction',
    description: 'Discover the Solarch architecture, its embedded dual-database design, and why running a TypeScript-native BaaS inside your Node process eliminates infrastructure friction.',
  },
  '/docs/overview/getting-started': {
    title: 'Getting Started with Solarch — Quickstart Guide',
    heading: 'Getting Started',
    description: 'Learn how to install Solarch, start an embedded backend server, define collections, and make your first CRUD API and authentication requests.',
  },
  '/docs/overview/installation': {
    title: 'Installing Solarch — npm, Docker & npx Setup',
    heading: 'Installation',
    description: 'Comprehensive installation options for Solarch including global CLI setup, programmatic npm package integration, Docker containers, and environment verification.',
  },
  '/docs/overview/project-structure': {
    title: 'Solarch Project Structure — Files & Persistent Storage',
    heading: 'Project Structure',
    description: 'Understand how Solarch organizes project files, migration scripts, hook definitions, and persistent SQLite databases in the pb_data directory.',
  },
  '/docs/overview/cli': {
    title: 'Solarch CLI Reference — Commands, Flags & Options',
    heading: 'CLI Reference',
    description: 'Complete command-line interface documentation for Solarch, covering serve, migrate, admin user creation, and development flags.',
  },

  // ==================== 2. SDKs ====================
  '/docs/sdks': {
    title: 'Solarch Client SDKs — Web, Mobile, Desktop & AI',
    heading: 'SDKs',
    description: 'Official client libraries and integrations for Solarch, including the TypeScript Web SDK, React Native, Electron, Tauri, AI Agents, and Model Context Protocol.',
  },
  '/docs/sdks/ai': {
    title: 'Solarch AI Agent SDK — Tools & Workflows',
    heading: 'AI Agent SDK',
    description: 'Build autonomous agents and deterministic tool pipelines on top of Solarch using the official @solarch/agents TypeScript SDK.',
  },
  '/docs/sdks/web': {
    title: 'Solarch Web SDK — TypeScript Client Reference',
    heading: 'Web SDK',
    description: 'Client-side TypeScript SDK for browser applications with auto-caching, realtime collection subscriptions, file uploads, and session management.',
  },
  '/docs/sdks/react-native': {
    title: 'Solarch React Native SDK — Mobile Client Guide',
    heading: 'React Native SDK',
    description: 'Build cross-platform mobile apps with Solarch using AsyncStorage session persistence, multipart file uploads, and mobile realtime listeners.',
  },
  '/docs/sdks/electron': {
    title: 'Solarch Electron SDK — Desktop Integration',
    heading: 'Electron SDK',
    description: 'Integrate Solarch with Electron desktop applications using secure IPC channels, context isolation, and local embedded SQLite storage.',
  },
  '/docs/sdks/tauri': {
    title: 'Solarch Tauri SDK — Rust & Desktop Integration',
    heading: 'Tauri SDK',
    description: 'Lightweight desktop application architecture pairing Solarch with Tauri, featuring secure invoke commands and native window lifecycle management.',
  },
  '/docs/sdks/mcp': {
    title: 'Solarch MCP Server — Model Context Protocol',
    heading: 'Model Context Protocol (MCP)',
    description: 'Expose Solarch collections, queries, and administrative actions directly to AI coding assistants like Claude Desktop, Cursor, and agent harnesses via MCP.',
  },

  // ==================== 3. CONFIGURATION ====================
  '/docs/configuration': {
    title: 'Solarch Server Configuration — Options & Environment',
    heading: 'Configuration',
    description: 'Configure Solarch server parameters, port bindings, database directory locations, encryption keys, and third-party integrations.',
  },
  '/docs/configuration/setup': {
    title: 'Programmatic Server Setup — createServer Options',
    heading: 'Server Setup',
    description: 'Initialize and customize Solarch programmatically using createServer, custom Express middleware, and lifecycle configuration options.',
  },
  '/docs/configuration/environment': {
    title: 'Environment Variables Reference — Solarch Server',
    heading: 'Environment Variables',
    description: 'Complete reference of environment variables supported by Solarch for database encryption, SMTP email delivery, S3 storage, and auth keys.',
  },

  // ==================== 4. API & ROUTING ====================
  '/docs/api-routing': {
    title: 'Solarch Routing & Express Integration — API Guide',
    heading: 'API & Routing',
    description: 'Extend Solarch with custom Express routes, typed controllers, modular middleware pipelines, and input validation schemas.',
  },
  '/docs/api-routing/routing': {
    title: 'Custom Express Routing — Solarch Endpoints',
    heading: 'Routing',
    description: 'Register custom HTTP GET, POST, PUT, and DELETE endpoints on the Solarch Express engine alongside auto-generated collection routes.',
  },
  '/docs/api-routing/controllers': {
    title: 'Request Controllers & Handlers — Solarch Architecture',
    heading: 'Controllers',
    description: 'Structure custom business logic cleanly using controller classes, typed request/response parameters, and database query helpers.',
  },
  '/docs/api-routing/middleware': {
    title: 'Express Middleware & Security Filters — Solarch',
    heading: 'Middleware',
    description: 'Attach custom authentication guards, CORS rules, request logging, and rate limiters to custom routes in Solarch.',
  },
  '/docs/api-routing/validation': {
    title: 'Data Validation & Error Handling — Solarch Express',
    heading: 'Validation',
    description: 'Validate request bodies, query parameters, and headers using Zod or custom schemas with standardized JSON error responses.',
  },

  // ==================== 5. DATA & SERVICES ====================
  '/docs/data-services': {
    title: 'Solarch Data Layer — SQLite Database & Services',
    heading: 'Data & Services',
    description: 'Explore Solarch embedded SQLite engine, 14 field types, dual-database architecture, and service layer domain logic patterns.',
  },
  '/docs/data-services/database': {
    title: 'Solarch SQLite Database — Schema & Configuration',
    heading: 'Database & Collections',
    description: 'Deep dive into Solarch embedded SQLite engine via better-sqlite3, Write-Ahead Logging (WAL) mode, PRAGMA tuning, and collection schema rules.',
  },
  '/docs/data-services/services': {
    title: 'Service Layer Pattern — Business Logic in Solarch',
    heading: 'Services',
    description: 'Encapsulate database queries and external integrations into reusable, injectable TypeScript domain services.',
  },

  // ==================== 6. SECURITY & AUTH ====================
  '/docs/security-auth': {
    title: 'Security & Authentication — Solarch Identity Engine',
    heading: 'Security & Auth',
    description: 'Comprehensive guide to Solarch authentication, JWT session management, OAuth2 providers, OTP, MFA/TOTP, and declarative access rules.',
  },
  '/docs/security-auth/authentication': {
    title: 'Solarch Authentication — OAuth2, OTP & MFA',
    heading: 'Authentication Engine',
    description: 'Configure user authentication in Solarch with email/password, OAuth2 providers (GitHub, Google), passwordless OTP, TOTP multi-factor, and JWT tokens.',
  },
  '/docs/security-auth/security': {
    title: 'Security Rules & Application Hardening — Solarch',
    heading: 'Security Rules',
    description: 'Protect collections with fine-grained access rules, request context macros (@request.auth), SQL injection guards, and Helmet security headers.',
  },

  // ==================== 7. HOSTING & DEPLOYMENT ====================
  '/docs/hosting-deployment': {
    title: 'Hosting & Deployment — Self-Hosting Solarch',
    heading: 'Hosting & Deployment',
    description: 'Deploy Solarch to production on VPS, Fly.io, Railway, Docker, or bare metal, complete with systemd, PM2, and automated test pipelines.',
  },
  '/docs/hosting-deployment/deployment': {
    title: 'Solarch Self-Hosted Deployment Guide — Production Setup',
    heading: 'Production Deployment',
    description: 'Step-by-step production deployment guide for Solarch using Docker, PM2, reverse proxies (Nginx/Caddy), persistent volume mounting, and SSL certificates.',
  },
  '/docs/hosting-deployment/testing': {
    title: 'Testing Solarch Applications — Vitest & Supertest',
    heading: 'Testing',
    description: 'Write fast integration and unit tests for Solarch endpoints and custom services using in-memory SQLite instances and Vitest.',
  },

  // ==================== 8. FILE STORAGE ====================
  '/docs/file-storage': {
    title: 'Solarch File Storage — Local Disk & S3 Storage',
    heading: 'File Storage',
    description: 'Manage file uploads, serving, thumbnail generation, protected download tokens, and S3-compatible cloud storage backends in Solarch.',
  },
  '/docs/file-storage/upload': {
    title: 'File Uploads & Attachments — Solarch Storage',
    heading: 'File Upload',
    description: 'Upload files and image attachments to collection records using multipart form data with automatic MIME validation and size limits.',
  },
  '/docs/file-storage/serve-files': {
    title: 'Serving Files & Dynamic Thumbnails — Solarch Storage',
    heading: 'Serve Files',
    description: 'Serve stored media files directly from local disk or S3 with on-the-fly sharp image resizing (JPEG, PNG, WebP, AVIF) and forced downloads.',
  },
  '/docs/file-storage/protected-tokens': {
    title: 'Protected File Tokens — Time-Limited Downloads',
    heading: 'Protected Tokens',
    description: 'Generate time-limited signed JWT file tokens to securely deliver private documents and media to authorized users.',
  },
  '/docs/file-storage/s3-configuration': {
    title: 'Storage Drivers & S3 Configuration — AWS S3, MinIO & R2',
    heading: 'S3 Configuration',
    description: 'Configure Solarch pluggable storage drivers, comparing local filesystem storage with AWS S3, Cloudflare R2, MinIO, or DigitalOcean Spaces.',
  },

  // ==================== 9. REALTIME ====================
  '/docs/realtime': {
    title: 'Solarch Realtime Engine — WebSockets & Event Subscriptions',
    heading: 'Realtime',
    description: 'Build reactive applications with Solarch WebSocket engine, Server-Sent Events, topic-based subscriptions, and automatic disconnect cleanup.',
  },
  '/docs/realtime/websocket': {
    title: 'Solarch WebSocket Realtime — Channels & Subscriptions',
    heading: 'WebSocket Engine',
    description: 'Connect client applications to Solarch high-throughput WebSocket server for live record changes, custom event broadcast, channel subscriptions, and automatic disconnect cleanup.',
  },
  '/docs/realtime/sse': {
    title: 'Server-Sent Events (SSE) — HTTP Event Streaming',
    heading: 'Server-Sent Events',
    description: 'Lightweight unidirectional event streaming over standard HTTP for client notifications and real-time dashboard feeds.',
  },
  '/docs/realtime/subscription-channels': {
    title: 'Subscription Channels & Topic Scoping — Realtime',
    heading: 'Subscription Channels',
    description: 'Subscribe to individual records, collection-wide events, or wildcard topics with granular authorization checks on each event.',
  },

  // ==================== 10. MIGRATIONS ====================
  '/docs/migrations': {
    title: 'Solarch Schema Migrations — Automated SQLite Versioning',
    heading: 'Migrations',
    description: 'Track, generate, and execute schema migrations automatically in Solarch, with safe rollback support and snapshot versioning.',
  },
  '/docs/migrations/directory-structure': {
    title: 'Migrations Directory Structure — pb_migrations Layout',
    heading: 'Directory Structure',
    description: 'File naming conventions and directory layout for snapshot and incremental migration files in the pb_migrations directory.',
  },
  '/docs/migrations/migration-file-format': {
    title: 'Migration File Syntax — Up & Down Definitions',
    heading: 'Migration File Format',
    description: 'Syntax and programmatic API for writing custom migration files to modify collections, run raw SQL, or transform existing data.',
  },
  '/docs/migrations/running-migrations': {
    title: 'Running Migrations — CLI & Automated Startup Execution',
    heading: 'Running Migrations',
    description: 'Execute pending migrations via CLI commands or enable automatic migration checks upon application startup.',
  },
  '/docs/migrations/rollback': {
    title: 'Rolling Back Migrations — Schema Reversal',
    heading: 'Rollback',
    description: 'Safely revert schema changes to previous versions using solarch migrate down and transactional rollback guarantees.',
  },

  // ==================== 11. JAVASCRIPT HOOKS ====================
  '/docs/javascript-hooks': {
    title: 'Solarch JavaScript Hooks — Sandboxed Backend Logic',
    heading: 'JavaScript Hooks',
    description: 'Write custom backend logic triggered by database and request lifecycle events inside a secure, isolated Node.js VM sandbox.',
  },
  '/docs/javascript-hooks/available-hook-events': {
    title: 'Database Lifecycle Hook Events — Solarch Hooks',
    heading: 'Available Hook Events',
    description: 'Complete list of lifecycle hooks including onRecordBeforeCreate, onRecordAfterUpdate, onRecordBeforeDelete, and auth event triggers.',
  },
  '/docs/javascript-hooks/available-globals': {
    title: 'Sandbox Globals & Helpers — $app, $db & $security',
    heading: 'Available Globals',
    description: 'Reference of global utilities and $app helpers exposed to hooks, detailing Node.js VM sandbox execution boundaries and security constraints.',
  },
  '/docs/javascript-hooks/examples': {
    title: 'JavaScript Hook Recipes & Examples — Solarch',
    heading: 'Examples',
    description: 'Practical code recipes for sending transactional emails, calculating derived fields, logging audits, and calling external webhooks.',
  },

  // ==================== 12. API REFERENCE ====================
  '/docs/api-reference': {
    title: 'Solarch REST API Reference — Endpoints & Specification',
    heading: 'API Reference',
    description: 'Complete HTTP REST API reference for Solarch, detailing authentication, collection schemas, record CRUD, and file endpoints.',
  },
  '/docs/api-reference/base-url': {
    title: 'API Base URL & Protocol Conventions — Solarch API',
    heading: 'Base URL',
    description: 'Base URL structure, URL encoding conventions, and response schemas for interacting with the Solarch HTTP REST API.',
  },
  '/docs/api-reference/collections': {
    title: 'Collections REST API — Schema Management Endpoints',
    heading: 'Collections API',
    description: 'REST endpoints for creating, inspecting, updating, and deleting collection definitions programmatically.',
  },
  '/docs/api-reference/records': {
    title: 'Records CRUD REST API — Querying, Filtering & Pagination',
    heading: 'Records API',
    description: 'HTTP endpoints for listing, creating, updating, and deleting records with filtering expressions, sorting, and field expansions.',
  },
  '/docs/api-reference/auth': {
    title: 'Auth REST API & Bearer Tokens — Password, OAuth, OTP & MFA',
    heading: 'Auth Endpoints',
    description: 'Complete endpoint and header specification for REST API authentication, Bearer tokens, token refresh, password resets, and TOTP MFA setup.',
  },
  '/docs/api-reference/admin': {
    title: 'Admin Management REST API — Administrative Endpoints',
    heading: 'Admin API',
    description: 'Administrative endpoints for server health diagnostics, backup generation, and global setting management.',
  },
  '/docs/api-reference/files': {
    title: 'Files REST API — Upload & Download Endpoints',
    heading: 'Files API',
    description: 'REST API documentation for multipart file uploads, token requests, and media retrieval.',
  },
  '/docs/api-reference/other': {
    title: 'System & Utility Endpoints — Health & Batch API',
    heading: 'Other Endpoints',
    description: 'System health checks, version information, and batch request processing endpoints in Solarch.',
  },
};
